import { ChangeEvent, useState } from "react";
import { CheckIcon, CloseIcon, QuestionMarkCircleIcon, RichText, TextInput, ToggleInput } from "@datagame/ui";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { Section } from "../../Common";
import { useGame } from "../../../../../../../contexts/game/GameContext";
import {
  LEADERBOARD_CONFIGURATION_UPDATE,
  SWIPER_CONFIGURATION_UPDATE,
} from "../../../../../../../graphql/mutations/GameMutation";
import { AttributesSection } from "../AttributesSection";
import { isSwiperGame } from "../../../../../../../graphql/helpers/SwiperGame";
import { Accordion } from "../../../../../../shared/ui/Accordion";
import { LeaderboardConfigSection } from "../LeaderboardConfigSection";
import { LeaderboardConfigurationUpdateInput } from "../../../../../../../gql/graphql";
import { isLeaderboardGame } from "../../../../../../../graphql/helpers/LeaderboardGame";
import { GAME_ALL } from "../../../../../../../graphql/queries/GameQueries";
import { useProject } from "../../../../../../../contexts/project/ProjectContext";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";
import { SwiperGame } from "../../../../../../../graphql/helpers/isSwiperGame";

import styles from "./SwiperSettingsForm.module.scss";

export const SwiperSettingsForm = () => {
  const { t } = useTranslation();
  const {
    currentGame: game,
    settings: { swiperQuestion, setSwiperQuestion, isSwiperShuffled, setIsSwiperShuffled },
  } = useGame();
  const { project } = useProject();

  const [showLeaderboard, setShowLeaderboard] = useState(
    isLeaderboardGame(game) ? game.configuration.showLeaderboard : false,
  );

  const [showSwipeCorrectness, setShowSwipeCorrectness] = useState(
    (game as SwiperGame | null)?.configuration.showSwipeCorrectness ?? false,
  );

  const [correctSwipeText, setCorrectSwipeText] = useState(
    (game as SwiperGame | null)?.configuration.correctSwipeText ?? "",
  );

  const [incorrectSwipeText, setIncorrectSwipeText] = useState(
    (game as SwiperGame | null)?.configuration.incorrectSwipeText ?? "",
  );

  const [updateConfig] = useMutation(SWIPER_CONFIGURATION_UPDATE);
  const [updateLeaderboardConfig] = useMutation(LEADERBOARD_CONFIGURATION_UPDATE);

  const handleSwipeQuestionChange = (value: string) => {
    setSwiperQuestion(value);
  };

  const handleShuffleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsSwiperShuffled(event.target.checked);

    updateConfig({
      variables: {
        swiperConfigurationId: game?.configuration.id,
        data: {
          shuffleCards: event.target.checked,
        },
      },
    });
  };

  const handleShowLeaderboardChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    setShowLeaderboard(checked);

    updateConfig({
      variables: {
        swiperConfigurationId: game?.configuration.id,
        data: {
          showLeaderboard: checked,
        },
      },
    });
  };

  const handleShowSwipeCorrectnessChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    setShowSwipeCorrectness(checked);

    updateConfig({
      variables: {
        swiperConfigurationId: game?.configuration.id,
        data: {
          showSwipeCorrectness: checked,
        },
      },
    });
  };

  const handleCorrectnessTextSave = () => {
    updateConfig({
      variables: {
        swiperConfigurationId: game?.configuration.id,
        data: {
          correctSwipeText,
          incorrectSwipeText,
        },
      },
    });
  };

  const handleLeaderboardConfigurationChange = (data: LeaderboardConfigurationUpdateInput) => {
    if (isSwiperGame(game) && project) {
      updateLeaderboardConfig({
        variables: {
          leaderboardConfigurationId: game?.configuration.leaderboardConfiguration?.id,
          data,
        },
        refetchQueries: [{ query: GAME_ALL, variables: { projectId: project.id } }],
      });
    }
  };

  const handleSaveQuestion = () => {
    updateConfig({
      variables: {
        swiperConfigurationId: game?.configuration.id,
        data: {
          question: swiperQuestion,
        },
      },
    });
  };

  if (!game) {
    return null;
  }

  return (
    <>
      <Section
        title={t("project:gameSettings.form.questionLabel")}
        tooltip={<TooltipWithIcon content={t("project:gameSettings.form.questionTooltip")} />}
      >
        <RichText
          icon={<QuestionMarkCircleIcon />}
          placeholder="Swipe right if you like it, left if you don't!"
          value={swiperQuestion}
          onChange={handleSwipeQuestionChange}
          onBlur={handleSaveQuestion}
          size="small"
          className={styles.input}
        />
      </Section>

      <AttributesSection game={game} />

      <Section title={t("project:gameSettings.form.preferences")}>
        <ToggleInput
          className={styles.toggleWrapper}
          value={isSwiperShuffled}
          name="shuffle"
          label="Shuffle"
          onChange={handleShuffleChange}
          tooltip={<TooltipWithIcon content={t("project:gameSettings.form.shuffleTooltip")} />}
        />

        <ToggleInput
          className={styles.toggleWrapper}
          value={showLeaderboard}
          name="show-leaderboard"
          label="Show leaderboard"
          onChange={handleShowLeaderboardChange}
          tooltip={<TooltipWithIcon content={t("project:gameSettings.form.showLeaderboardTooltip")} />}
        />

        {showLeaderboard && (
          <Accordion title={t("project:gameSettings.form.leaderboardConfig")}>
            {isSwiperGame(game) && (
              <LeaderboardConfigSection
                leaderboardConfiguration={game.configuration.leaderboardConfiguration}
                onUpdate={handleLeaderboardConfigurationChange}
              />
            )}
          </Accordion>
        )}
      </Section>

      <Section title={t("project:gameSettings.form.correctnessIndicatorSection")}>
        <ToggleInput
          className={styles.toggleWrapper}
          value={showSwipeCorrectness}
          name="show-swipe-correctness"
          label={t("project:gameSettings.form.showSwipeCorrectness") as string}
          onChange={handleShowSwipeCorrectnessChange}
          tooltip={<TooltipWithIcon content={t("project:gameSettings.form.showSwipeCorrectnessTooltip")} />}
        />

        {showSwipeCorrectness && (
          <div className={styles.correctnessInputsWrapper}>
            <Section
              className={styles.correctnessInputSection}
              title={t("project:gameSettings.form.incorrectSwipeTextSection")}
            >
              <TextInput
                placeholder="Incorrect"
                size="small"
                name="incorrect-swipe-text"
                value={incorrectSwipeText}
                className={styles.input}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setIncorrectSwipeText(e.target.value)}
                onBlur={handleCorrectnessTextSave}
                icon={<CloseIcon />}
              />
            </Section>

            <Section
              className={styles.correctnessInputSection}
              title={t("project:gameSettings.form.correctSwipeTextSection")}
            >
              <TextInput
                placeholder="Correct"
                size="small"
                name="correct-swipe-text"
                value={correctSwipeText}
                className={styles.input}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setCorrectSwipeText(e.target.value)}
                onBlur={handleCorrectnessTextSave}
                icon={<CheckIcon />}
              />
            </Section>
          </div>
        )}
      </Section>
    </>
  );
};
